import { ArrowDown, ArrowUp, Check, Copy, KeyRound, Loader2, Plus, RotateCw, ShieldCheck, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CallingLockedCard } from '@/features/calling/components/CallingLockedCard'
import {
  useAgentMutations,
  useAgents,
  useCallingMe,
  useDispositionMutations,
  useDispositions,
  useProviderConfig,
  useRotateWebhookToken,
  useSaveProviderConfig,
  useTestProviderConnection,
} from '@/hooks/useCalling'
import { useUrlState } from '@/hooks/useUrlState'
import type { Agent, CallPurpose, Disposition, DispositionAppliesTo, ProviderConfig, WebhookToken } from '@/types/api/calling'
import { getErrorMessage } from '@/utils/error-parser'

/** Workspace telephony: the Exotel account, call dispositions and agents' phone numbers. */
export function TelephonySettingsPage() {
  const { data: me, isLoading } = useCallingMe()
  const [{ tab }, setUrl] = useUrlState('', { tab: 'provider' })

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Telephony</h1>
        <p className="mt-1 text-neutral-500">Connect your Exotel account so agents can call leads from records.</p>
      </div>
      {isLoading || !me ? (
        <Skeleton className="h-64 w-full rounded-xl" />
      ) : !me.featureEnabled ? (
        <CallingLockedCard />
      ) : (
        <Tabs value={tab} onValueChange={(value) => setUrl({ tab: value })}>
          <TabsList>
            <TabsTrigger value="provider">Provider</TabsTrigger>
            <TabsTrigger value="dispositions">Dispositions</TabsTrigger>
            <TabsTrigger value="agents">Agents</TabsTrigger>
          </TabsList>
          <TabsContent value="provider" className="mt-4"><ProviderTab /></TabsContent>
          <TabsContent value="dispositions" className="mt-4"><DispositionsTab /></TabsContent>
          <TabsContent value="agents" className="mt-4"><AgentsTab /></TabsContent>
        </Tabs>
      )}
    </div>
  )
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface ProviderForm {
  enabled: boolean
  accountSid: string
  apiHost: string
  apiKey: string
  apiToken: string
  callerIdService: string
  callerIdPromotional: string
  defaultPurpose: CallPurpose
  recordCalls: boolean
  verificationAppId: string
}

function formFrom(config: ProviderConfig | undefined): ProviderForm {
  return {
    enabled: config?.id ? config.enabled : true,
    accountSid: config?.accountSid ?? '',
    apiHost: config?.apiHost ?? config?.allowedApiHosts[0] ?? 'api.exotel.com',
    apiKey: '',
    apiToken: '',
    callerIdService: config?.callerIdService ?? '',
    callerIdPromotional: config?.callerIdPromotional ?? '',
    defaultPurpose: config?.defaultPurpose ?? 'SERVICE',
    recordCalls: config?.recordCalls ?? false,
    verificationAppId: config?.verificationAppId ?? '',
  }
}

function ProviderTab() {
  const { data: config, isLoading } = useProviderConfig()
  const save = useSaveProviderConfig()
  const test = useTestProviderConnection()
  const rotate = useRotateWebhookToken()
  const [form, setForm] = useState<ProviderForm>(() => formFrom(undefined))
  const [freshWebhook, setFreshWebhook] = useState<WebhookToken | null>(null)
  const [confirmRotate, setConfirmRotate] = useState(false)

  // Fill the form once per config (first load, or the config just created); later refetches (e.g.
  // after rotating the webhook URL) must not wipe edits in progress.
  const hydratedFor = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (config && hydratedFor.current !== config.id) {
      hydratedFor.current = config.id
      setForm(formFrom(config))
    }
  }, [config])

  if (isLoading || !config) return <Skeleton className="h-96 w-full rounded-xl" />

  const isNew = !config.id
  const set = <K extends keyof ProviderForm>(key: K, value: ProviderForm[K]) => setForm((f) => ({ ...f, [key]: value }))

  const submit = () => {
    save.mutate(
      {
        enabled: form.enabled,
        accountSid: form.accountSid.trim(),
        apiHost: form.apiHost,
        // Secrets are only shown masked: blank keeps the stored value.
        apiKey: form.apiKey.trim() || undefined,
        apiToken: form.apiToken.trim() || undefined,
        callerIdService: form.callerIdService.trim() || undefined,
        callerIdPromotional: form.callerIdPromotional.trim() || undefined,
        defaultPurpose: form.defaultPurpose,
        recordCalls: form.recordCalls,
        verificationAppId: form.verificationAppId.trim() || undefined,
      },
      {
        onSuccess: (saved) => {
          toast.success('Telephony settings saved')
          if (saved.newWebhook) setFreshWebhook(saved.newWebhook)
          setForm((f) => ({ ...f, apiKey: '', apiToken: '' }))
        },
        onError: (error) => toast.error(getErrorMessage(error, 'Could not save the settings')),
      }
    )
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            Exotel account {config.id && (config.enabled ? <Badge variant="success">On</Badge> : <Badge variant="secondary">Off</Badge>)}
          </CardTitle>
          <CardDescription>Your own Exotel account. Calls are billed by Exotel to it.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2">
            <div>
              <p className="text-sm font-medium">Click-to-call enabled</p>
              <p className="text-xs text-neutral-500">Turn off to stop new calls without losing the settings.</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} aria-label="Click-to-call enabled" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Account SID" htmlFor="sid">
              <input id="sid" value={form.accountSid} onChange={(e) => set('accountSid', e.target.value)} className={inputClass} />
            </Field>
            <Field label="Region (API host)" htmlFor="host">
              <Select value={form.apiHost} onValueChange={(v) => set('apiHost', v)}>
                <SelectTrigger id="host"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {config.allowedApiHosts.map((host) => (
                    <SelectItem key={host} value={host}>{host === 'api.in.exotel.com' ? 'Mumbai · api.in.exotel.com' : `Singapore · ${host}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="API key" htmlFor="key" hint={config.apiKeyMasked ? `Saved: ${config.apiKeyMasked}. Leave blank to keep.` : undefined}>
              <input id="key" value={form.apiKey} onChange={(e) => set('apiKey', e.target.value)} className={inputClass} autoComplete="off" />
            </Field>
            <Field label="API token" htmlFor="token" hint={config.apiTokenSet ? 'Saved. Leave blank to keep.' : undefined}>
              <input id="token" type="password" value={form.apiToken} onChange={(e) => set('apiToken', e.target.value)} className={inputClass} autoComplete="new-password" />
            </Field>
            <Field label="Caller ID for service calls" htmlFor="cid-service" hint="ExoPhone, 160-series">
              <input id="cid-service" value={form.callerIdService} onChange={(e) => set('callerIdService', e.target.value)} className={inputClass} placeholder="+918047…" />
            </Field>
            <Field label="Caller ID for promotional calls" htmlFor="cid-promo" hint="ExoPhone, 140-series (optional)">
              <input id="cid-promo" value={form.callerIdPromotional} onChange={(e) => set('callerIdPromotional', e.target.value)} className={inputClass} />
            </Field>
            <Field label="Default purpose" htmlFor="purpose">
              <Select value={form.defaultPurpose} onValueChange={(v) => set('defaultPurpose', v as CallPurpose)}>
                <SelectTrigger id="purpose"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SERVICE">Service</SelectItem>
                  <SelectItem value="PROMOTIONAL">Promotional</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Verification flow (app ID)" htmlFor="app-id" hint="Exotel app that greets agents verifying their number">
              <input id="app-id" value={form.verificationAppId} onChange={(e) => set('verificationAppId', e.target.value)} className={inputClass} />
            </Field>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2">
            <div>
              <p className="text-sm font-medium">Record calls</p>
              <p className="text-xs text-neutral-500">Exotel keeps the recordings; the CRM stores their link.</p>
            </div>
            <Switch checked={form.recordCalls} onCheckedChange={(v) => set('recordCalls', v)} aria-label="Record calls" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={submit} disabled={save.isPending || !form.accountSid || (isNew && (!form.apiKey || !form.apiToken))}>
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
            <Button
              variant="outline"
              disabled={isNew || test.isPending}
              onClick={() =>
                test.mutate(undefined, {
                  onSuccess: (result) => (result.ok ? toast.success(result.message) : toast.error(result.message)),
                })
              }
            >
              {test.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
              Test connection
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><KeyRound className="h-5 w-5" /> Webhooks</CardTitle>
          <CardDescription>Exotel reports call status here. The secret part of the URL is shown only once.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {config.webhookConfigured ? (
            <>
              <UrlRow label="Status callback (set per call automatically)" url={config.statusWebhookUrlMasked} />
              <UrlRow label="Inbound logging (Passthru applet)" url={config.inboundWebhookUrlMasked} />
              <Button variant="outline" size="sm" onClick={() => setConfirmRotate(true)}>
                <RotateCw className="mr-2 h-4 w-4" /> Generate new URL
              </Button>
            </>
          ) : (
            <p className="text-neutral-500">Save the account to generate the webhook URL.</p>
          )}
          <ol className="list-decimal space-y-1 pl-4 text-xs text-neutral-600">
            <li>Status callbacks are set on every call; nothing to configure in Exotel.</li>
            <li>To log inbound calls, add a <b>Passthru</b> applet at the start of your inbound call flow with the inbound URL (GET).</li>
            <li>For phone verification, create a short greeting flow and enter its app ID.</li>
          </ol>
        </CardContent>
      </Card>

      {freshWebhook && <WebhookDialog webhook={freshWebhook} onClose={() => setFreshWebhook(null)} />}
      <Dialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate a new webhook URL?</DialogTitle>
            <DialogDescription>The current URL stops working immediately. Update the Passthru applet in Exotel afterwards.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRotate(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={rotate.isPending}
              onClick={() =>
                rotate.mutate(undefined, {
                  onSuccess: (webhook) => {
                    setConfirmRotate(false)
                    setFreshWebhook(webhook)
                  },
                  onError: (error) => toast.error(getErrorMessage(error, 'Could not generate a new URL')),
                })
              }
            >
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const inputClass =
  'w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400'

function Field({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-xs font-medium text-neutral-600">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-neutral-400">{hint}</p>}
    </div>
  )
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Copy"
      onClick={async () => {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
    </Button>
  )
}

function UrlRow({ label, url, copy = false }: { label: string; url: string | null; copy?: boolean }) {
  if (!url) return null
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-neutral-600">{label}</p>
      <div className="flex items-center gap-1 rounded-lg bg-neutral-50 px-2 py-1.5">
        <code className="min-w-0 flex-1 break-all text-[11px] text-neutral-700">{url}</code>
        {copy && <CopyButton value={url} />}
      </div>
    </div>
  )
}

function WebhookDialog({ webhook, onClose }: { webhook: WebhookToken; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Copy your webhook URLs now</DialogTitle>
          <DialogDescription>They contain a secret and won't be shown again. Generate a new one if you lose them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <UrlRow label="Inbound logging (Passthru applet, GET)" url={webhook.inboundUrl} copy />
          <UrlRow label="Status callback (for reference)" url={webhook.statusCallbackUrl} copy />
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Dispositions ─────────────────────────────────────────────────────────────

const APPLIES_TO_LABEL: Record<DispositionAppliesTo, string> = {
  CONNECTED: 'Answered calls',
  NOT_CONNECTED: 'Unanswered calls',
  ANY: 'Any call',
}

function DispositionsTab() {
  const [showInactive, setShowInactive] = useState(false)
  const { data: dispositions = [], isLoading } = useDispositions(true)
  const { create, update, deactivate, reorder } = useDispositionMutations()
  const visible = dispositions.filter((d) => showInactive || d.active)

  const move = (index: number, delta: number) => {
    const ids = visible.map((d) => d.id)
    const target = index + delta
    if (target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    reorder.mutate(ids)
  }

  const save = (d: Disposition, patch: Partial<Disposition>) => {
    const next = { ...d, ...patch }
    update.mutate(
      {
        id: d.id,
        data: {
          label: next.label,
          colorHex: next.colorHex ?? undefined,
          appliesTo: next.appliesTo,
          requiresNote: next.requiresNote,
          createsFollowUp: next.createsFollowUp,
          active: next.active,
        },
      },
      { onError: (error) => toast.error(getErrorMessage(error, 'Could not save the disposition')) }
    )
  }

  if (isLoading) return <Skeleton className="h-64 w-full rounded-xl" />

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-lg">Dispositions</CardTitle>
          <CardDescription>What agents pick after a call. Removing one keeps it on past calls.</CardDescription>
        </div>
        <label className="flex items-center gap-2 text-xs text-neutral-600">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} aria-label="Show removed" /> Show removed
        </label>
      </CardHeader>
      <CardContent className="space-y-2">
        {visible.map((d, index) => (
          <DispositionRow
            key={d.id}
            disposition={d}
            onSave={(patch) => save(d, patch)}
            onRemove={() => deactivate.mutate(d.id)}
            onUp={index > 0 ? () => move(index, -1) : undefined}
            onDown={index < visible.length - 1 ? () => move(index, 1) : undefined}
          />
        ))}
        <Button
          variant="outline"
          onClick={() =>
            create.mutate(
              { label: 'New disposition', appliesTo: 'ANY', requiresNote: false, createsFollowUp: false, colorHex: '#64748b' },
              { onError: (error) => toast.error(getErrorMessage(error, 'Could not add the disposition')) }
            )
          }
        >
          <Plus className="mr-2 h-4 w-4" /> Add disposition
        </Button>
      </CardContent>
    </Card>
  )
}

function DispositionRow({
  disposition,
  onSave,
  onRemove,
  onUp,
  onDown,
}: {
  disposition: Disposition
  onSave: (patch: Partial<Disposition>) => void
  onRemove: () => void
  onUp?: () => void
  onDown?: () => void
}) {
  const [label, setLabel] = useState(disposition.label)
  useEffect(() => setLabel(disposition.label), [disposition.label])

  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-xl border border-neutral-200 p-2 ${disposition.active ? '' : 'opacity-60'}`}>
      <input
        type="color"
        value={disposition.colorHex ?? '#64748b'}
        onChange={(e) => onSave({ colorHex: e.target.value })}
        className="h-8 w-8 cursor-pointer rounded border border-neutral-200"
        aria-label="Color"
      />
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={() => label.trim() && label !== disposition.label && onSave({ label: label.trim() })}
        className="min-w-[10rem] flex-1 rounded-lg border border-neutral-200 px-2 py-1.5 text-sm"
        aria-label="Label"
        maxLength={100}
      />
      <Select value={disposition.appliesTo} onValueChange={(v) => onSave({ appliesTo: v as DispositionAppliesTo })}>
        <SelectTrigger className="h-9 w-40" aria-label="Applies to"><SelectValue /></SelectTrigger>
        <SelectContent>
          {(Object.keys(APPLIES_TO_LABEL) as DispositionAppliesTo[]).map((key) => (
            <SelectItem key={key} value={key}>{APPLIES_TO_LABEL[key]}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <label className="flex items-center gap-1.5 text-xs text-neutral-600">
        <input type="checkbox" checked={disposition.requiresNote} onChange={(e) => onSave({ requiresNote: e.target.checked })} />
        Note required
      </label>
      <label className="flex items-center gap-1.5 text-xs text-neutral-600">
        <input type="checkbox" checked={disposition.createsFollowUp} onChange={(e) => onSave({ createsFollowUp: e.target.checked })} />
        Follow-up
      </label>
      <div className="ml-auto flex items-center">
        <Button variant="ghost" size="icon" aria-label="Move up" disabled={!onUp} onClick={onUp}><ArrowUp className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" aria-label="Move down" disabled={!onDown} onClick={onDown}><ArrowDown className="h-4 w-4" /></Button>
        {disposition.active ? (
          <Button variant="ghost" size="icon" aria-label="Remove" onClick={onRemove}><Trash2 className="h-4 w-4" /></Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => onSave({ active: true })}>Restore</Button>
        )}
      </div>
    </div>
  )
}

// ─── Agents ───────────────────────────────────────────────────────────────────

function AgentsTab() {
  const { data: agents = [], isLoading } = useAgents()
  if (isLoading) return <Skeleton className="h-64 w-full rounded-xl" />
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Agents</CardTitle>
        <CardDescription>
          The number Exotel rings for each member. Agents verify their own number from My phone, or you can verify it here.
        </CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-neutral-100">
        {agents.map((agent) => <AgentRow key={agent.userId} agent={agent} />)}
      </CardContent>
    </Card>
  )
}

function AgentRow({ agent }: { agent: Agent }) {
  const { setPhone, verify } = useAgentMutations()
  const [phone, setPhoneValue] = useState(agent.phone ?? '')
  useEffect(() => setPhoneValue(agent.phone ?? ''), [agent.phone])

  return (
    <div className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-[12rem] flex-1">
        <p className="text-sm font-medium">{agent.name ?? agent.email}</p>
        <p className="text-xs text-neutral-500">{agent.email}</p>
      </div>
      <input
        value={phone}
        onChange={(e) => setPhoneValue(e.target.value)}
        onBlur={() =>
          phone !== (agent.phone ?? '') &&
          setPhone.mutate(
            { userId: agent.userId, data: { phone: phone.trim() || undefined } },
            { onError: (error) => toast.error(getErrorMessage(error, 'Could not save the number')) }
          )
        }
        placeholder="No number"
        aria-label={`Phone of ${agent.name ?? agent.email}`}
        className="w-44 rounded-lg border border-neutral-200 px-2 py-1.5 text-sm"
      />
      {agent.verified ? (
        <Badge variant="success">Verified</Badge>
      ) : (
        <Button
          variant="outline"
          size="sm"
          disabled={!agent.phone || verify.isPending}
          onClick={() =>
            verify.mutate(agent.userId, {
              onSuccess: () => toast.success('Number verified'),
              onError: (error) => toast.error(getErrorMessage(error, 'Could not verify')),
            })
          }
        >
          Mark verified
        </Button>
      )}
    </div>
  )
}
